import {Button, Flex, Group, Text, Textarea} from "@mantine/core";
import {useRef, useState, useEffect} from "react";
import {getHotkeyHandler, useMediaQuery} from "@mantine/hooks";
import {useAppSelector} from "../../../app/hooks";
import {useTranslation} from "react-i18next";
import {abortStream} from "../chatApi";
import {
    selectActiveThread,
    selectIsActiveThreadStreaming,
} from "../threadsSlice";
import IconArrowRight from "../../../assets/arrow-right.svg?react";
import IconArrowUp from "../../../assets/arrow-up.svg?react";
import IconBackslash from "../../../assets/backslash.svg?react";
import IconPlayerStop from "../../../assets/player-stop.svg?react";
import classes from "./CommandInput.module.css";
import {useInputWrapping} from "../hooks/useInputWrapping";
import {usePasteAttachments} from "../hooks/usePasteAttachments";
import {subscribeToDraftRestore} from "../utils/draftRestore";
import {PasteChipList} from "../../../components/PasteChip";
import {SubmitWithAttachments} from "../utils/pasteAttachments";

interface CommandInputProps {
    variant: "splash" | "chat";
    onSendMessage: SubmitWithAttachments;
    onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onChange?(value: string): void;
    placeholder?: string;
    disabled?: boolean;
    isTriggerSearch?: boolean;
    atCapacity?: boolean;
    onBlockedSubmit?: () => void;
}

export function CommandInput({
    variant,
    onSendMessage,
    onChange,
    onKeyDown,
    placeholder,
    disabled = false,
    isTriggerSearch = false,
    atCapacity = false,
    onBlockedSubmit,
}: CommandInputProps) {
    const {t} = useTranslation();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [value, setValue] = useState("");
    const [isShaking, setIsShaking] = useState(false);
    const shakeTimeoutRef = useRef<number | null>(null);
    const isDesktop = useMediaQuery("(min-width: $mantine-breakpoint-sm)");
    const isStreaming = useAppSelector(selectIsActiveThreadStreaming);
    const activeThread = useAppSelector(selectActiveThread);
    const activeThreadId = activeThread?.id;
    const {
        attachments,
        handlePaste,
        removeAttachment,
        clearAttachments,
        restoreAttachments,
    } = usePasteAttachments();

    useEffect(() => {
        if (variant !== "chat") return;
        return subscribeToDraftRestore(
            ({entityId, message, attachments: restored}) => {
                if (entityId !== activeThreadId) return;
                setValue((current) =>
                    current.trim() === "" ? message : current
                );
                restoreAttachments((current) =>
                    current.length === 0 ? restored : current
                );
            }
        );
    }, [variant, restoreAttachments, activeThreadId]);

    const {wrapperRef, hiddenRef, buttonGroupRef, shouldWrap} = useInputWrapping(value);

    // Auto-focus: always for splash, desktop-only for chat
    useEffect(() => {
        if (variant === "splash" || isDesktop) {
            inputRef.current?.focus();
        }
    }, [isDesktop, variant]);

    const handleOnChange = (value: string) => {
        setValue(value);
        onChange?.(value);
    };

    const triggerShake = () => {
        setIsShaking(true);
        if (shakeTimeoutRef.current) {
            window.clearTimeout(shakeTimeoutRef.current);
        }
        shakeTimeoutRef.current = window.setTimeout(() => {
            setIsShaking(false);
        }, 320);
    };

    const handleSubmit = () => {
        if (value.trim() === "") {
            return;
        }
        if (atCapacity) {
            triggerShake();
            onBlockedSubmit?.();
            return;
        }
        if (disabled || isStreaming) {
            return;
        }
        if (onSendMessage(value, attachments) === false) {
            return;
        }
        setValue("");
        clearAttachments();
        if (isDesktop) {
            inputRef.current?.focus();
        }
    };

    const cancelStreaming = () => {
        if (activeThread?.streamId) {
            abortStream(activeThread.streamId);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented) {
            const hotkeyHandler = getHotkeyHandler([["enter", handleSubmit]]);
            hotkeyHandler(event);
        }
    };

    // Determine variant-specific classes
    const triggerClass = isTriggerSearch ? classes.triggerSearchActive : "";
    const outerGroupClass = `${classes.outerGroup} ${classes[`outerGroup--${variant}`]} ${triggerClass}`;
    const wrapClass = shouldWrap ? classes.outerGroupWrap : "";
    const inputClass = `${classes.input} ${classes[`input--${variant}`]}`;

    const finalPlaceholder =
        placeholder ||
        (variant === "splash"
            ? t("common.askQuestion")
            : t("input.placeholder.thread"));

    const isButtonDisabled = atCapacity
        ? false
        : isStreaming
          ? false
          : disabled || value.trim() === "";
    const buttonAction = atCapacity
        ? handleSubmit
        : isStreaming
          ? cancelStreaming
          : handleSubmit;
    const buttonTitle = atCapacity ? "At capacity" : isStreaming ? "Stop" : "Send";
    const sendButtonClass = [
        classes.sendButton,
        atCapacity ? classes.sendButtonAtCapacity : "",
        isShaking ? classes.sendButtonShake : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <Flex
            ref={wrapperRef}
            className={`${outerGroupClass} ${wrapClass}`}
            gap="xs"
            onClick={() => inputRef.current?.focus()}
        >
            <PasteChipList
                chips={attachments}
                onRemove={removeAttachment}
                announce
            />
            <Textarea
                ref={inputRef}
                className={classes.textareaWrapper}
                classNames={{
                    input: inputClass,
                }}
                placeholder={finalPlaceholder}
                aria-label={finalPlaceholder}
                value={value}
                autosize
                minRows={1}
                autoFocus={variant === "splash" || !!isDesktop}
                maxRows={13}
                onChange={(e) => handleOnChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
            />
            <Text ref={hiddenRef} className={classes.hiddenText}>
                {value}
            </Text>
            <Group
                ref={buttonGroupRef}
                className={classes.buttonGroup}
                gap="xs"
                wrap="nowrap"
                justify="flex-end"
            >
                <Button
                    className={sendButtonClass}
                    classNames={{
                        inner: classes.sendButtonInner,
                    }}
                    variant="subtle"
                    onClick={buttonAction}
                    disabled={isButtonDisabled}
                    aria-disabled={atCapacity || undefined}
                    title={buttonTitle}
                >
                    {isStreaming ? (
                        <IconPlayerStop className={classes.icon} />
                    ) : (
                        <>
                            <IconArrowRight
                                className={`${classes.icon} ${classes.iconArrowRight}`}
                            />
                            <IconArrowUp
                                className={`${classes.icon} ${classes.iconArrowUp}`}
                            />
                            {atCapacity && (
                                <IconBackslash
                                    className={`${classes.icon} ${classes.iconBackslash}`}
                                />
                            )}
                        </>
                    )}
                </Button>
            </Group>
        </Flex>
    );
}
